/**
 * Reglas puras (sin Angular) de los errores de la web que se reportan al back
 * en `POST /monitoring/client-errors`. Aqui vive todo lo que decide QUE se
 * manda: el saneo (sin datos personales ni tokens), la firma para deduplicar
 * y el tope por sesion. El servicio solo arma la peticion.
 *
 * Contrato del back: { mensaje ≤500, pila? ≤4000, ruta ≤300, version? ≤50,
 * navegador? ≤200, origen: 'web' }. El back vuelve a sanear; esto es la
 * primera barrera para que nada sensible salga del navegador.
 */

export const LIMITES = { mensaje: 500, pila: 4000, ruta: 300, navegador: 200 } as const;

/** Mismo error (misma firma) a lo mas una vez por minuto. */
export const VENTANA_DEDUPE_MS = 60_000;
/** A lo mas 10 reportes por sesion del navegador. */
export const TOPE_POR_SESION = 10;

export interface ReporteErrorCliente {
    mensaje: string;
    pila?: string;
    ruta: string;
    navegador?: string;
    origen: 'web';
}

/** Rutas de la web cuyo siguiente segmento es un token secreto (enlaces publicos). */
const PREFIJOS_CON_TOKEN = new Set(['mi-tanda', 'puntualidad', 'unirse', 'co-organizar', 'invitacion', 'invitaciones', 'invite', 'reset-password', 'share']);

function recortar(texto: string, max: number): string {
    return texto.length > max ? texto.slice(0, max) : texto;
}

/**
 * Deja solo la forma de la ruta: sin query ni fragmento, y todo segmento que
 * no sea una palabra fija de la aplicacion (minusculas con guiones) se cambia
 * por `:x`. Asi `/mi-tanda/Ab12...` y `/tanda/3f2c-...` no llevan tokens ni ids.
 */
export function sanearRuta(ruta: string | null | undefined): string {
    if (!ruta) return '/';
    let r = String(ruta);
    // Si viene una URL completa, solo interesa el path.
    const url = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i.exec(r);
    if (url) r = r.slice(url[0].length);
    r = r.split(/[?#]/)[0] || '/';
    const segmentos = r.split('/');
    let siguienteEsToken = false;
    const limpios = segmentos.map((s) => {
        if (s === '') return s;
        if (siguienteEsToken) {
            siguienteEsToken = false;
            return ':x';
        }
        let seg = s;
        try { seg = decodeURIComponent(s); } catch { /* se queda como viene */ }
        const fijo = /^[a-z][a-z_-]{0,39}$/.test(seg) || /^v\d{1,2}$/.test(seg);
        if (!fijo) return ':x';
        if (PREFIJOS_CON_TOKEN.has(seg)) siguienteEsToken = true;
        return seg;
    });
    const salida = limpios.join('/') || '/';
    return recortar(salida.startsWith('/') ? salida : '/' + salida, LIMITES.ruta);
}

/**
 * Quita de un texto libre (mensaje o pila) lo que pueda identificar a alguien
 * o abrir algo: correos, JWT, `Bearer ...`, parametros `token=`, cadenas
 * largas que parecen llaves, telefonos y el query/ids de las URL.
 */
export function sanearTexto(texto: unknown, max: number): string {
    if (texto === null || texto === undefined) return '';
    let t = String(texto);
    t = t.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[correo]');
    t = t.replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '[token]');
    t = t.replace(/\b(Bearer|Basic)\s+[^\s"',]+/gi, '$1 [token]');
    t = t.replace(/\b(token|access_token|share_token|key|apikey|api_key|password|code|sig|signature)=([^&\s"',]+)/gi, '$1=[token]');
    // URLs: se conserva el origen (sirve para saber si fue la API o un chunk)
    // y la forma del path; el query y los ids se van.
    t = t.replace(/\b(https?:\/\/[^/\s"'()<>]+)(\/[^\s"'()<>]*)?/gi, (_m, origen: string, path?: string) => {
        if (!path) return origen;
        // Archivos del propio bundle (main-ABC123.js:1:234): se dejan para leer la pila.
        const archivo = /^(\/[\w.-]+)+\.(js|mjs|css|map)(:\d+)*$/.exec(path.split(/[?#]/)[0]);
        return origen + (archivo ? path.split(/[?#]/)[0] : sanearRuta(path));
    });
    t = t.replace(/\b[A-Za-z0-9_-]{32,}\b/g, '[token]');
    t = t.replace(/\+?\d[\d\s().-]{7,}\d/g, (m) => (/^\d+:\d+$/.test(m) ? m : '[num]'));
    return recortar(t.trim(), max);
}

/**
 * Firma para deduplicar: el mensaje sin numeros (ids, lineas, requestId) y la
 * ruta ya saneada. Dos fallas iguales en la misma pantalla cuentan como una.
 */
export function firmaError(mensaje: string, ruta: string): string {
    return (mensaje || '').replace(/\[[a-z]+\]/g, '').replace(/[0-9a-f]{8,}/gi, '').replace(/\d+/g, '#').slice(0, 200) + '|' + ruta;
}

/**
 * Decide si un error HTTP merece reporte: solo fallas del servidor (5xx) o
 * de red (0) contra la propia API, y nunca el envio del propio reporte (sin
 * bucles). Los 4xx son reglas de negocio y no se reportan. Un 0 con el
 * navegador sin conexion es la red de la persona, no TandaPlus.
 */
export function esHttpReportable(status: number, url: string, apiUrl: string, enLinea = true): boolean {
    if (!url || !apiUrl || !url.startsWith(apiUrl)) return false;
    if (url.split(/[?#]/)[0].endsWith('/monitoring/client-errors')) return false;
    if (status === 0) return enLinea;
    return status >= 500 && status <= 599;
}

/** Mensaje de un error HTTP: estado, metodo y la forma del path (sin query, sin ids). */
export function mensajeHttp(status: number, metodo: string, url: string, requestId?: string | null): string {
    const id = requestId && /^[A-Za-z0-9-]{8,64}$/.test(requestId) ? ` requestId=${requestId}` : '';
    return recortar(`HTTP ${status} ${(metodo || 'GET').toUpperCase()} ${sanearRuta(url)}${id}`, LIMITES.mensaje);
}

/** Saca mensaje y pila de lo que sea que llego al ErrorHandler. */
export function describirError(error: unknown): { mensaje: string; pila?: string } {
    // Angular envuelve las promesas rechazadas: el error real esta en `rejection`.
    const e: any = (error as any)?.rejection ?? error;
    let mensaje: string;
    let pila: string | undefined;
    if (e instanceof Error) {
        mensaje = `${e.name}: ${e.message}`;
        pila = typeof e.stack === 'string' ? e.stack : undefined;
    } else if (typeof e === 'string') {
        mensaje = e;
    } else {
        try { mensaje = JSON.stringify(e) ?? String(e); } catch { mensaje = String(e); }
    }
    const pilaLimpia = pila ? sanearTexto(pila, LIMITES.pila) : '';
    return {
        mensaje: sanearTexto(mensaje, LIMITES.mensaje) || 'Error',
        ...(pilaLimpia ? { pila: pilaLimpia } : {}),
    };
}

/**
 * Limitador del navegador: misma firma a lo mas una vez por ventana y un tope
 * total por sesion. `conteoInicial` permite seguir el tope entre recargas
 * (sessionStorage); si no hay almacenamiento, el tope es por carga de pagina.
 */
export class LimitadorErrores {
    private ultimos = new Map<string, number>();
    private enviados: number;

    constructor(
        conteoInicial = 0,
        private readonly tope = TOPE_POR_SESION,
        private readonly ventanaMs = VENTANA_DEDUPE_MS,
    ) {
        this.enviados = Number.isFinite(conteoInicial) && conteoInicial > 0 ? Math.floor(conteoInicial) : 0;
    }

    get total(): number {
        return this.enviados;
    }

    /** true si se puede mandar ahora; si si, lo cuenta. */
    permitir(firma: string, ahora: number): boolean {
        if (this.enviados >= this.tope) return false;
        const previo = this.ultimos.get(firma);
        if (previo !== undefined && ahora - previo < this.ventanaMs) return false;
        this.ultimos.set(firma, ahora);
        this.enviados++;
        // Que el mapa no crezca sin fin si una pantalla genera firmas distintas.
        if (this.ultimos.size > 100) {
            for (const [f, t] of this.ultimos) if (ahora - t >= this.ventanaMs) this.ultimos.delete(f);
        }
        return true;
    }
}

/** Arma el cuerpo final con los limites del contrato. */
export function construirReporte(
    datos: { mensaje: string; pila?: string },
    rutaActual: string,
    navegador?: string | null,
): ReporteErrorCliente {
    const reporte: ReporteErrorCliente = {
        mensaje: recortar(datos.mensaje || 'Error', LIMITES.mensaje),
        ruta: sanearRuta(rutaActual),
        origen: 'web',
    };
    if (datos.pila) reporte.pila = recortar(datos.pila, LIMITES.pila);
    const nav = (navegador || '').trim();
    if (nav) reporte.navegador = recortar(nav, LIMITES.navegador);
    return reporte;
}
